import i18n from '../../../shared/locales/i18n';
import { documentService } from '../../../shared/services/api/document.service';
import { notificationEventTypes } from '../../../shared/enums/notificationEventTypes';

export interface DocumentNotificationLike {
  documentId?: number;
  notificationEventType?: string;
}

export const useDocumentReadStatus = () => {
  const isDocumentUnread = (documentId: number, notifications: DocumentNotificationLike[]): boolean =>
    notifications.some(
      notification =>
        notification.documentId === documentId &&
        notification.notificationEventType === notificationEventTypes.NEW_OR_UPDATED_SECURE_DOCUMENT
    );

  /**
   * Multiselect / single-item rule:
   * - all unread → mark as read
   * - all read or mixed → mark as unread
   */
  const shouldMarkAsRead = (documentIds: number[], notifications: DocumentNotificationLike[]): boolean =>
    documentIds.length > 0 && documentIds.every(documentId => isDocumentUnread(documentId, notifications));

  const getToggleReadLabel = (documentIds: number[], notifications: DocumentNotificationLike[]): string =>
    shouldMarkAsRead(documentIds, notifications)
      ? i18n.t('document.mark_as_read').toString()
      : i18n.t('document.mark_as_unread').toString();

  const markAsRead = async (documentIds: number[], institutionProfileIds: number[]): Promise<void> => {
    await documentService.markAsRead({ documentIds, institutionProfileIds });
  };

  const markAsUnread = async (documentIds: number[], institutionProfileIds: number[]): Promise<void> => {
    await documentService.markAsUnread({ documentIds, institutionProfileIds });
  };

  const toggleReadStatus = async (
    documentIds: number[],
    institutionProfileIds: number[],
    notifications: DocumentNotificationLike[]
  ): Promise<'read' | 'unread'> => {
    if (shouldMarkAsRead(documentIds, notifications)) {
      await markAsRead(documentIds, institutionProfileIds);
      return 'read';
    }

    await markAsUnread(documentIds, institutionProfileIds);
    return 'unread';
  };

  return {
    isDocumentUnread,
    shouldMarkAsRead,
    getToggleReadLabel,
    toggleReadStatus,
  };
};
